import styled from '@emotion/styled';
import { BaseButton, Card, TypeFilterChip, Variables } from '@enter/core';
import Icon from '@mdi/react';

const V = Variables.ThemeLight;

/*
 * The "Active Claims By Responsibility" card above the claims table (FE-151).
 *
 * Every clickable mark — bar segment, responsibility chip, category chip, the None pill — is core's
 * `TypeFilterChip`, a real <button> that toggles a filter, restyled here because its own look is
 * for a grey tab bar. Responsibility colours come from `getResponsibilityColors`, the palette the
 * table's Responsibility badges use, so the widget and the rows below it agree.
 */

export const Panel = styled(Card)`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px 24px;
    width: 100%;
    box-sizing: border-box;
`;

export const Header = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 16px;
`;

export const TitleBlock = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 10px;
    min-width: 0;
`;

export const Title = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${V.TextStrong};
    white-space: nowrap;
`;

export const Totals = styled.span`
    font-size: 14px;
    color: ${V.TextMild};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

export const Controls = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-left: auto;
`;

/* ---- Date range ------------------------------------------------------------------------- */

export const RangeButton = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
`;

export const RangeButtonIcon = styled(Icon)`
    width: 16px;
    height: 16px;
    fill: ${V.TextMild};
`;

export const RangeButtonPrefix = styled.span`
    color: ${V.TextMild};
    font-weight: 400;
`;

export const CustomRange = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 280px;
    padding: 10px 16px 8px;
    border-top: 1px solid ${V.Border1};
`;

export const Caption = styled.span`
    font-size: 12px;
    color: ${V.TextXMild};
`;

export const DateInputs = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px;
`;

export const DateField = styled.label`
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    color: ${V.TextMild};
`;

export const RangeError = styled.span`
    font-size: 12px;
    color: ${V.WarningInverted3};
`;

/* ---- Metric toggle and None pill -------------------------------------------------------- */

const chipReset = `
    font-family: inherit;
    font-variant-numeric: tabular-nums;
    box-shadow: none;
`;

export const NonePill = styled(TypeFilterChip)`
    ${chipReset}
    gap: 6px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 400;
    border-radius: 4px;
    color: ${V.TextMild};
    background: ${props => (props.active ? V.Background1 : 'transparent')};
    border: 1px solid ${props => (props.active ? V.Border1 : 'transparent')};

    &:hover {
        background: ${V.Background1};
    }

    strong {
        font-weight: 600;
        color: ${V.TextMedium};
    }
`;

/* ---- Main bar --------------------------------------------------------------------------- */

export const Bar = styled.div<{ height: number }>`
    display: flex;
    gap: 2px;
    height: ${props => props.height}px;
    border-radius: ${props => (props.height > 10 ? 4 : 3)}px;
    overflow: hidden;
    background: ${V.Background1};
`;

export const Segment = styled(TypeFilterChip)<{ share: number; fill: string; dimmed?: boolean }>`
    ${chipReset}
    /*
     * Grow in proportion to the share from a zero basis, rather than width: N%. Percent widths sum
     * to 100% before the 2px gaps and the 3px floor are added, so the bar overflowed and its
     * overflow: hidden cut off the smallest segment — a 0.3% Payer vanished on sandbox (2026-10-06).
     */
    flex: ${props => props.share} 1 0px;
    /* A 0.1% group still has to be visible and clickable. */
    min-width: 3px;
    height: 100%;
    padding: 0;
    border-radius: 0;
    background: ${props => props.fill};
    opacity: ${props => (props.dimmed ? 0.3 : 1)};
    transition: flex-grow 180ms ease-out, opacity 120ms ease-out;

    &:hover {
        filter: brightness(0.92);
    }
`;

/** Text for a screen reader on a mark that shows only colour. */
export const VisuallyHidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
`;

/* ---- Responsibility chips --------------------------------------------------------------- */

export const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

export const GroupChip = styled(TypeFilterChip)`
    ${chipReset}
    gap: 8px;
    padding: 6px 12px 6px 6px;
    border-radius: 8px;
    font-weight: 400;
    border: 1px solid ${props => (props.active ? V.PrimaryConstant6 : V.Border1)};
    box-shadow: ${props => (props.active ? `inset 0 0 0 1px ${V.PrimaryConstant6}` : 'none')};
    background: ${props => (props.active ? V.PrimaryTranslated1 : V.Background3)};
    transition: background 120ms ease-out, border-color 120ms ease-out;

    &:hover {
        background: ${V.PrimaryTranslated1};
    }
`;

export const Primary = styled.span`
    font-size: 14px;
    color: ${V.TextStrong};
`;

export const Secondary = styled.span`
    font-size: 13px;
    color: ${V.TextMild};
`;

/* ---- Category breakdown ----------------------------------------------------------------- */

export const Breakdown = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 14px;
    border-top: 1px solid ${V.Border1};
`;

export const BreakdownTitle = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${V.TextXMild};

    span {
        font-size: 12px;
        font-weight: 400;
        letter-spacing: normal;
        text-transform: none;
    }
`;

export const CategoryChip = styled(TypeFilterChip)`
    ${chipReset}
    align-items: flex-start;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 8px;
    font-weight: 400;
    text-align: left;
    border: 1px solid ${props => (props.active ? V.PrimaryConstant6 : V.Border1)};
    background: ${props => (props.active ? V.PrimaryTranslated1 : V.Background3)};
    color: ${V.TextMedium};

    &:hover {
        filter: brightness(0.97);
    }
`;

export const Swatch = styled.span<{ fill: string }>`
    flex: none;
    width: 10px;
    height: 10px;
    margin-top: 4px;
    border-radius: 2px;
    background: ${props => props.fill};
`;

export const CategoryIcon = styled(Icon)<{ active?: boolean }>`
    flex: none;
    width: 18px;
    height: 18px;
    fill: ${props => (props.active ? V.PrimaryInverted3 : V.TextMedium)};
`;

export const CategoryText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 1px;
`;

export const CategoryLabel = styled.span<{ active?: boolean }>`
    font-size: 14px;
    font-weight: 600;
    color: ${props => (props.active ? V.PrimaryInverted3 : V.TextMedium)};
`;

export const CategoryStat = styled.span`
    font-size: 12px;
    color: ${V.TextMild};
`;

export const CategoryCode = styled.span`
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10.5px;
    color: ${V.TextXMild};
`;

/* ---- States ----------------------------------------------------------------------------- */

/** Dims the figures while the next range loads; they stay readable rather than vanishing. */
export const Body = styled.div<{ stale: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 16px;
    opacity: ${props => (props.stale ? 0.55 : 1)};
    transition: opacity 120ms ease-out;
`;

export const Footnote = styled.span`
    font-size: 12px;
    color: ${V.TextXMild};
`;
